import type {ReactNode} from "react";

type V = number | string | null;

export declare type Option<T extends V = V> = {
    id: T
    label: string
    subLabel?: string
    subTotalSpend?: number
    /** Shown before the label, e.g. a country flag */
    icon?: ReactNode
    obj: any
}

type OptionId<T extends V = V> = Pick<Option<T>, 'id'>;

export declare type SelectionResult<T extends V = V> = {
    /**
     * If it's everything including the selection, or everything excluding the selection
     */
    inclusive: boolean;
    selected: OptionId<T>[];
}

export type OptionPage = {
    search: string
    page: number
    window: Option[]
    total: number
}

export namespace MultiSelection {
    export const NONE: SelectionResult<any> = {inclusive: true, selected: []}
    export const ALL: SelectionResult<any> = {inclusive: false, selected: []}

    export function exclusiveToggle<T extends V = V>(s: SelectionResult<T> | null | undefined, element: T): SelectionResult<T> | null {
        if (!s) {
            return {
                inclusive: false,
                selected: [{id: element}],
            }
        }
        const index = s.selected.findIndex((o) => o.id === element);
        if (s.inclusive) {
            // Remove it from the inclusive selection
            if (index === -1) {
                // If not found, do nothing
                return null
            } else {
                // If found, remove it
                const newSelected = [...s.selected];
                newSelected.splice(index, 1);
                return {
                    inclusive: true,
                    selected: newSelected,
                }
            }
        } else {
            // If not inclusive, toggle the selection
            if (index === -1) {
                // If not found, add it
                return {
                    inclusive: false,
                    selected: [...s.selected, {id: element}],
                }
            } else {
                // If found, remove it
                const newSelected = [...s.selected];
                newSelected.splice(index, 1);
                return {
                    inclusive: false,
                    selected: newSelected,
                }
            }
        }
    }

    /**
     * Toggle the selection to inclusive if not already, or toggle the element in the inclusive selection.
     * @param s
     * @param element
     */
    export function inclusiveToggle<T extends V = V>(s: SelectionResult<T> | null | undefined, element: T): SelectionResult<T> {
        if (!s || !s.inclusive) {
            // If not inclusive, toggle to inclusive
            return {
                inclusive: true,
                selected: [{id: element}],
            }
        }
        const index = s.selected.findIndex((o) => o.id === element);
        if (index === -1) {
            // If not found, add it
            return {
                inclusive: true,
                selected: [...s.selected, {id: element}],
            }
        } else {
            // If found, remove it
            const newSelected = [...s.selected];
            newSelected.splice(index, 1);
            return {
                inclusive: true,
                selected: newSelected,
            }
        }
    }

    export function inclusiveSet<T extends V = V>(s: SelectionResult<T> | null | undefined, element: T): SelectionResult<T> | null {
        if (s && s.inclusive && s.selected.length === 1 && s.selected[0].id === element) {
            // If inclusive and already selected, no change
            return null;
        }
        return {
            inclusive: true,
            selected: [{id: element}],
        }

    }

    export function inclusiveSetAll<T extends V = V>(s: SelectionResult<T> | null | undefined, elements: T[]): SelectionResult<T> | null {
        if (s && s.inclusive) {
            // If inclusive and already selected, no change
            if (s.selected.length === elements.length && s.selected.every((o) => elements.includes(o.id))) {
                return null;
            }
        }
        return {
            inclusive: true,
            selected: elements.map(id => ({id})),
        }

    }

    function addSelectedElements<T extends V = V>(s: SelectionResult<T>, elements: T[]): SelectionResult<T> {
        const existingIds = new Set(s.selected.map(o => o.id));
        const newSelected = elements.filter(id => !existingIds.has(id)).map(id => ({id}));
        if (newSelected.length > 0) {
            return {
                inclusive: s.inclusive,
                selected: [...s.selected, ...newSelected],
            }
        } else {
            return s; // No change
        }
    }

    function removeSelectedElements<T extends V = V>(s: SelectionResult<T>, elements: T[]): SelectionResult<T> {
        if (!s || s.selected.length === 0) {
            return s; // No change
        }
        const newSelected = s.selected.filter(o => !elements.includes(o.id));
        if (newSelected.length === s.selected.length) {
            return s; // No change
        }
        return {
            inclusive: s.inclusive,
            selected: newSelected,
        }
    }

    export function removeSelection<T extends V = V>(s: SelectionResult<T>, elements: T[]): SelectionResult<T> {
        if (s.inclusive) {
            return removeSelectedElements(s, elements);
        } else {
            return addSelectedElements(s, elements);
        }
    }

    export function addSelection<T extends V = V>(s: SelectionResult<T>, elements: T[]): SelectionResult<T> {
        if (s.inclusive) {
            return addSelectedElements(s, elements);
        } else {
            return removeSelectedElements(s, elements);
        }
    }

    export function isSet<T extends V = V>(s: SelectionResult<T> | null | undefined): s is SelectionResult<T> {
        if (!s) return false;
        return s.selected.length > 0;
    }

    export function isActive(s: Partial<SelectionResult> | undefined | null): boolean {
        return (s?.selected?.length ?? 0) > 0
    }

    export function isIncludingOne(s?: Partial<SelectionResult>): boolean {
        return (s?.selected?.length ?? 0) === 1 && Boolean(s?.inclusive)
    }
}
